// Fenster des Edit-Bereichs: Strip Silence (mit Live-Vorschau in der Timeline), Fades, Repeat, Spot, Clip Gain,
// Umbenennen, Takte einfügen/entfernen, Memory Locations, Clip-Loop, Event Operations (MIDI), Tastenkürzel.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Clip } from '@srs/shared';
import { clipAudio, processAudio, toBuffer, SUITE_OPS, type SuiteOp } from '../audio/audiosuite.ts';
import { STRETCH_MODES, type StretchMode } from '../audio/stretch.ts';
import { pluginDef } from '../audio/plugins.ts';
import { quantizeElastic, setElastic, setPitch } from '../edit/elasticops.ts';
import { planTiming, type TimingPlan, type TimingTarget } from '../audio/vocalscan.ts';
import { ensureWarp } from '../edit/warp.ts';
import { uid } from '../state/demo.ts';
import { commitTracks } from '../audio/render.ts';
import { defaultRange, exportAudio, stemItems, type StemMode } from '../audio/export.ts';
import { api } from '../api/client.ts';
import { encodeWav } from '../audio/recorder.ts';
import type { FadeShape, Project } from '@srs/shared';
import { useStudio } from '../state/store.ts';
import { useEdit } from '../state/edit.ts';
import { engine } from '../audio/engine.ts';
import { toast } from '../components/ui.tsx';
import { fadeCurve } from '../audio/segments.ts';
import { detectSound } from '../edit/analysis.ts';
import { beatConform, beatSeparate, beatSmooth, classify, conformTarget, detectRange, tempoFromPerformance, tempoFromSelection, type BeatOpts, type Onset } from '../edit/beat.ts';
import { quantizeNotes, changeVelocity, changeDuration, transpose, DEFAULT_QUANTIZE, type VelocityMode, type DurationMode } from '../edit/midiops.ts';
import * as op from '../edit/ops.ts';
import { barAtSec, barToBeat, bbt, bpmAt, meterAt, meterAtBar, meterEvents, secAt, secOfBar, spbAt, tempoEvents } from '@srs/shared';
import { changeTempoAt, deleteMeter, deleteTempo, putMeter, putTempo, replaceTempos } from '../edit/tempoops.ts';
import { COMMANDS, keyLabel, targetClips, currentRange, run } from '../edit/commands.ts';
import { fmtBBT, fmtLen, parseTime } from './Editor.tsx';

export function EditDialogs() {
  const d = useEdit((s) => s.dialog);
  if (!d) return null;
  const close = () => useEdit.getState().open(null);
  switch (d) {
    case 'strip': return <StripSilence onClose={close} />;
    case 'fades': return <Fades onClose={close} />;
    case 'repeat': return <Repeat onClose={close} />;
    case 'spot': return <Spot onClose={close} />;
    case 'gain': return <Gain onClose={close} />;
    case 'rename': return <Rename onClose={close} />;
    case 'time': return <TimeOps onClose={close} />;
    case 'markers': return <Markers onClose={close} />;
    case 'loop': return <Loop onClose={close} />;
    case 'events': return <Events onClose={close} />;
    case 'shortcuts': return <Shortcuts onClose={close} />;
    case 'beat': return <BeatDetective onClose={close} />;
    case 'tempo': return <TempoMeter onClose={close} />;
    case 'audiosuite': return <AudioSuite onClose={close} />;
    case 'equant': return <ElasticQuantize onClose={close} />;
    case 'vtiming': return <VocalTiming onClose={close} />;
    case 'commit': return <CommitDialog onClose={close} />;
    case 'export': return <ExportDialog onClose={close} />;
  }
}

function Shell({ title, sub, onClose, children, wide, side }: { title: string; sub?: string; onClose: () => void; children: React.ReactNode; wide?: boolean; side?: boolean }) {
  const [pos, setPos] = useState(() => side ? { x: window.innerWidth - 470, y: 140 } : { x: Math.max(20, window.innerWidth / 2 - (wide ? 330 : 220)), y: 120 });
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); onClose(); } };
    window.addEventListener('keydown', esc, true);
    return () => window.removeEventListener('keydown', esc, true);
  }, [onClose]);
  return (
    <div className={`dlg ${wide ? 'wide' : ''}`} style={{ left: pos.x, top: pos.y }} onKeyDown={(e) => e.stopPropagation()}>
      <div className="pw-head"
        onPointerDown={(e) => { if ((e.target as HTMLElement).closest('button')) return; drag.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y }; (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); }}
        onPointerMove={(e) => { const g = drag.current; if (g) setPos({ x: Math.max(0, g.px + e.clientX - g.x), y: Math.max(0, g.py + e.clientY - g.y) }); }}
        onPointerUp={() => { drag.current = null; }}>
        <div className="pw-title"><b>{title}</b>{sub && <span>{sub}</span>}</div>
        <div className="r"><button className="btn small" title="Schließen (Esc)" onClick={onClose}>✕</button></div>
      </div>
      <div className="dlg-body">{children}</div>
    </div>
  );
}

function Slider({ label, value, min, max, step = 1, unit, onChange, fmt }: { label: string; value: number; min: number; max: number; step?: number; unit: string; onChange: (v: number) => void; fmt?: (v: number) => string }) {
  return (
    <label className="sl">
      <span>{label}</span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
      <input className="input num" type="number" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value))))} />
      <em>{fmt ? fmt(value) : unit}</em>
    </label>
  );
}

const srcLen = (id: string) => engine.buffers.get(id)?.duration;
const channels = (b: AudioBuffer) => Array.from({ length: b.numberOfChannels }, (_, i) => b.getChannelData(i));

// ---------------------------------------------------------------------------
// Beat Detective: Erkennen, Clip Separation, Clip Conform, Edit Smoothing, Tempo aus Auswahl
type BeatTab = 'detect' | 'conform' | 'smooth' | 'tempo';
function BeatDetective({ onClose }: { onClose: () => void }) {
  const o = useEdit((s) => s.beat);
  const range = useEdit((s) => s.range);
  const project = useStudio((s) => s.project);
  const commit = useStudio((s) => s.commit);
  const [tab, setTab] = useState<BeatTab>('detect');
  const [onsets, setOnsets] = useState<{ list: Onset[]; clips: number; missing: number }>({ list: [], clips: 0, missing: 0 });
  const [bars, setBars] = useState(0);
  const set = (p: Partial<BeatOpts>) => useEdit.getState().set({ beat: { ...o, ...p } });
  const [per, setPer] = useState<'bar' | 'beat'>('beat');
  const sel = range && !op.isEmptyRange(range) ? range : null;
  // Erkennung, wenn sich Auswahl, Audio oder Analyse ändern (verzögert)
  const audioKey = useMemo(() => project.clips.filter((c) => c.kind === 'audio' && sel?.trackIds.includes(c.trackId)).map((c) => `${c.id}:${c.start}:${c.duration}:${c.offset}:${c.sourceId}`).join('|'), [project.clips, sel]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (!sel) { setOnsets({ list: [], clips: 0, missing: 0 }); return; }
      const r = detectRange(useStudio.getState().project, sel, (id) => engine.buffers.get(id), o.emphasis);
      setOnsets({ list: r.onsets, clips: r.clips, missing: r.missing });
    }, 80);
    return () => clearTimeout(t);
  }, [audioKey, sel?.start, sel?.end, sel?.trackIds.join(','), o.emphasis]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (sel) setBars(Math.max(1, Math.round(barAtSec(project, sel.end) - barAtSec(project, sel.start)))); }, [sel?.start, sel?.end]); // eslint-disable-line react-hooks/exhaustive-deps
  const triggers = useMemo(() => classify(project, onsets.list, o), [project.bpm, project.beatsPerBar, project.tempos, project.meters, onsets, o.sensitivity, o.resolution, o.sub]); // eslint-disable-line react-hooks/exhaustive-deps
  // Tempo aus der Aufnahme: gefundene Takte bzw. Schläge
  const perf = useMemo(() => (tab === 'tempo' && sel ? tempoFromPerformance(project, onsets.list, sel, bars, per, o.sensitivity) : null), [tab, sel?.start, sel?.end, bars, per, onsets, o.sensitivity, project.tempos, project.bpm]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab === 'tempo') { const n = per === 'bar' ? 1 : meterAt(project, sel?.start ?? 0).num; useEdit.getState().set({ beatPreview: (perf?.points ?? []).map((t, i) => ({ t, cls: i % n === 0 ? 'bar' : 'beat' })) }); return; }
    useEdit.getState().set({ beatPreview: triggers.map((t) => ({ t: t.t, cls: t.cls, to: tab === 'conform' ? conformTarget(project, t, o) : undefined })) });
  }, [triggers, tab, perf, o.strength, o.excludeWithin, o.swing]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => useEdit.getState().set({ beatPreview: null }), []);
  const counts = { bar: triggers.filter((t) => t.cls === 'bar').length, beat: triggers.filter((t) => t.cls === 'beat').length, sub: triggers.filter((t) => t.cls === 'sub').length };
  const dev = triggers.length ? triggers.reduce((a, t) => a + Math.abs(t.t - t.grid), 0) / triggers.length * 1000 : 0;
  const need = () => { if (!sel) { toast('Erst einen Bereich auf den Audiospuren auswählen (am besten ganze Takte)'); return false; } if (!triggers.length) { toast('Keine Anschläge gefunden. Empfindlichkeit erhöhen oder Auflösung ändern.'); return false; } return true; };
  const doSeparate = () => { if (!need()) return; let n = 0; commit('Beat Detective: Trennen', (p) => { n = beatSeparate(p, sel!, triggers, o); }); toast(`An ${n} Anschlägen auf ${sel!.trackIds.length} ${sel!.trackIds.length === 1 ? 'Spur' : 'Spuren'} geteilt`); };
  const doConform = () => { if (!need()) return; let n = 0; commit('Beat Detective: Conform', (p) => { n = beatConform(p, sel!, triggers, o); }); toast(`${n} Stücke aufs Raster gezogen. Jetzt Glätten füllt die Lücken.`); setTab('smooth'); };
  const doSmooth = () => { if (!sel) return toast('Erst einen Bereich auswählen'); let r = { filled: 0, crossfades: 0 }; commit('Beat Detective: Glätten', (p) => { r = beatSmooth(p, sel, o, srcLen); }); toast(`${r.filled} Lücken gefüllt${o.smooth === 'xfade' ? `, ${r.crossfades} Crossfades` : ''}`); };
  const doAll = () => { if (!need()) return; let n = 0; commit('Beat Detective', (p) => { n = beatConform(p, sel!, triggers, o); beatSmooth(p, sel!, o, srcLen); }); toast(`Beat Detective: ${n} Stücke aufs Raster gezogen, Lücken gefüllt${o.smooth === 'xfade' ? ' und überblendet' : ''}. Rückgängig mit ⌘Z.`); };
  const capture = () => {
    if (!sel) return;
    const ba = Math.round(barAtSec(project, sel.start)), a = secOfBar(project, ba), b = secOfBar(project, Math.max(ba + 1, Math.round(barAtSec(project, sel.end))));
    useEdit.getState().setRange({ ...sel, start: a, end: b });
  };
  const subs = op.GRIDS.filter((g) => ['1/4', '1/8', '1/16', '1/32', '1/8T', '1/16T'].includes(g.id));
  const bpm = sel ? tempoFromSelection(project, sel, bars) : project.bpm;
  return (
    <Shell title="Beat Detective" sub="Event > Beat Detective (⌘8)" onClose={onClose} side>
      <div className="stack" style={{ gap: 10 }}>
        <div className="bd-sel">
          {sel
            ? <><span>Auswahl {fmtBBT(project, sel.start)} bis {fmtBBT(project, sel.end)}</span><span className="t2">{sel.trackIds.length} {sel.trackIds.length === 1 ? 'Spur' : 'Spuren'} · {onsets.clips} Clips</span><button className="lnk" title="Auswahl auf ganze Takte runden (Capture Selection)" onClick={capture}>auf Takte</button></>
            : <span className="t2">Erst in der Timeline einen Bereich auf den Audiospuren auswählen, bei Drums alle Mikrofone zusammen.</span>}
        </div>
        <div className="seg bd-tabs">
          {([['detect', 'Erkennen'], ['conform', 'Conform'], ['smooth', 'Glätten'], ['tempo', 'Tempo']] as [BeatTab, string][]).map(([k, l]) => <button key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{l}</button>)}
        </div>
        {(tab === 'detect' || tab === 'conform') && <>
          <label className="sl"><span>Analyse</span>
            <select className="input" value={o.emphasis} onChange={(e) => set({ emphasis: e.target.value as BeatOpts['emphasis'] })} style={{ gridColumn: 'span 3' }}>
              <option value="enhanced">Erweitert (alle Bänder)</option><option value="high">Hohe Frequenzen (Snare, Hi-Hat)</option><option value="low">Tiefe Frequenzen (Kick, Bass)</option>
            </select>
          </label>
          <Slider label="Empfindlichkeit" value={o.sensitivity} min={0} max={100} unit="%" onChange={(v) => set({ sensitivity: v })} />
          <label className="sl"><span>Auflösung</span>
            <div className="seg">
              {([['bar', 'Takte'], ['beat', 'Schläge'], ['sub', 'Unterteilung']] as [BeatOpts['resolution'], string][]).map(([k, l]) => <button key={k} className={o.resolution === k ? 'on' : ''} onClick={() => set({ resolution: k })}>{l}</button>)}
            </div>
            <select className="input" style={{ gridColumn: 'span 2' }} value={o.sub} onChange={(e) => set({ sub: e.target.value })} disabled={o.resolution !== 'sub'}>{subs.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}</select>
          </label>
        </>}
        {tab === 'detect' && <>
          <Slider label="Trigger Pad" value={o.padMs} min={0} max={50} unit="ms" onChange={(v) => set({ padMs: v })} />
          <div className="t2" style={{ fontSize: 12 }}>
            {sel ? <>{triggers.length} Anschläge: {counts.bar} auf Takten, {counts.beat} auf Schlägen, {counts.sub} dazwischen. Mittlere Abweichung vom Raster {dev.toFixed(1).replace('.', ',')} ms.</> : null}
            {onsets.missing > 0 && ` ${onsets.missing} Clips sind noch nicht geladen.`}
          </div>
          <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
            <button className="btn small" title="Alle gewählten Spuren an den Anschlägen teilen (Clip Separation)" onClick={doSeparate}>Trennen</button>
            <button className="btn small primary" title="Trennen, aufs Raster ziehen und glätten in einem Schritt" onClick={doAll}>Alles in einem Schritt</button>
          </div>
        </>}
        {tab === 'conform' && <>
          <Slider label="Stärke" value={o.strength} min={0} max={100} unit="%" onChange={(v) => set({ strength: v })} />
          <Slider label="Ausschließen" value={o.excludeWithin} min={0} max={100} unit="%" onChange={(v) => set({ excludeWithin: v })} />
          <Slider label="Swing" value={o.swing} min={0} max={150} unit="%" onChange={(v) => set({ swing: v })} />
          <div className="t2" style={{ fontSize: 12 }}>Blaue Marken zeigen, wohin jeder Anschlag rückt. Stärke unter 100 % lässt etwas vom Feel übrig, Ausschließen lässt alles in Ruhe, was schon nah am Raster liegt. Swing gilt für jede zweite Unterteilung.</div>
          <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
            <button className="btn small primary" title="Trennt bei Bedarf und zieht die Stücke aufs Raster (Clip Conform)" onClick={doConform}>Conform</button>
          </div>
        </>}
        {tab === 'smooth' && <>
          <div className="seg">
            <button className={o.smooth === 'fill' ? 'on' : ''} onClick={() => set({ smooth: 'fill' })}>Lücken füllen</button>
            <button className={o.smooth === 'xfade' ? 'on' : ''} onClick={() => set({ smooth: 'xfade' })}>Füllen und überblenden</button>
          </div>
          {o.smooth === 'xfade' && <Slider label="Crossfade" value={o.xfadeMs} min={0} max={50} step={0.5} unit="ms" onChange={(v) => set({ xfadeMs: v })} />}
          <div className="t2" style={{ fontSize: 12 }}>Zieht das Ende jedes Stücks bis zum nächsten Anschlag, damit keine Löcher bleiben. Der Crossfade liegt vor dem Schnitt, der Anschlag bleibt unberührt.</div>
          <div className="row" style={{ justifyContent: 'flex-end' }}><button className="btn small primary" onClick={doSmooth}>Glätten</button></div>
        </>}
        {tab === 'tempo' && <>
          <div className="t2" style={{ fontSize: 12 }}>Für frei eingespielte Aufnahmen: Auswahl vom ersten Anschlag auf der Eins bis zur Eins nach dem letzten Takt legen und die Zahl der Takte angeben.</div>
          <label className="sl"><span>Auswahl umfasst</span><input className="input num" style={{ width: 70, justifySelf: 'start' }} type="number" min={1} max={256} value={bars} onChange={(e) => setBars(Math.max(1, Number(e.target.value) || 1))} /><em style={{ gridColumn: 'span 2', fontSize: 12, color: 'var(--ink)' }}>Takte, im Schnitt {String(bpm).replace('.', ',')} BPM</em></label>
          <div className="bd-opt">
            <b>Raster folgt der Aufnahme</b>
            <span className="t2">Jeder {per === 'bar' ? 'Takt' : 'Schlag'} bekommt sein eigenes Tempo, das Raster liegt danach genau auf dem Spiel. Aufnahme und MIDI bleiben, wo sie sind. Die Linien zeigen die gefundenen {per === 'bar' ? 'Takte' : 'Schläge'}.</span>
            <div className="row" style={{ gap: 6 }}>
              <div className="seg">
                <button className={per === 'beat' ? 'on' : ''} onClick={() => setPer('beat')}>je Schlag</button>
                <button className={per === 'bar' ? 'on' : ''} onClick={() => setPer('bar')}>je Takt</button>
              </div>
              <button className="btn small primary" style={{ marginLeft: 'auto' }} disabled={!perf} onClick={() => {
                if (!perf || !sel) return;
                const from = perf.leadIn !== undefined ? 0 : perf.events[0].beat, to = perf.events[perf.events.length - 1].beat;
                const bad = perf.events.find((e) => e.bpm < 30 || e.bpm > 400);
                if (bad) return toast(`Ein Abschnitt ergäbe ${Math.round(bad.bpm)} BPM. Auswahl genau auf die Eins legen oder die Taktzahl prüfen.`);
                commit('Tempo aus Aufnahme', (p) => replaceTempos(p, from, to + 1e-6, perf.events));
                toast(`Raster folgt der Aufnahme: ${perf.events.length - (perf.leadIn !== undefined ? 1 : 0)} Tempowechsel ab Takt ${perf.bar0 + 1}${perf.leadIn !== undefined ? `, Vorlauf ${String(perf.leadIn).replace('.', ',')} BPM` : ''}. Rückgängig mit ⌘Z.`);
              }}>Raster anpassen</button>
            </div>
          </div>
          <div className="bd-opt">
            <b>Ein Tempo für den Song</b>
            <span className="t2">Setzt das Tempo ab der Stelle der Auswahl auf den Durchschnitt. MIDI und Marker bleiben auf ihren Takten.</span>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button className="btn small" disabled={!sel} onClick={() => { const v = Math.max(20, Math.min(300, bpm)); commit('Tempo aus Auswahl', (p) => changeTempoAt(p, sel!.start, v)); toast(`Tempo ${String(v).replace('.', ',')} BPM übernommen`); }}>Tempo übernehmen</button>
            </div>
          </div>
        </>}
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Strip Silence (Kap. 39)
function StripSilence({ onClose }: { onClose: () => void }) {
  const opts = useEdit((s) => s.strip);
  const range = useEdit((s) => s.range);
  const clips = useStudio((s) => s.project.clips);
  const commit = useStudio((s) => s.commit);
  const [naming, setNaming] = useState<{ on: boolean; name: string; start: number; zeros: number; suffix: string }>({ on: false, name: 'Clip ', start: 1, zeros: 1, suffix: '' });
  const set = (p: Partial<typeof opts>) => useEdit.getState().set({ strip: { ...opts, ...p } });
  // Erkennung für alle Audio-Clips in der Auswahl, verzögert, damit Ziehen an den Reglern flüssig bleibt
  const sound = useMemo(() => new Map<string, [number, number][]>(), []);
  const [count, setCount] = useState({ clips: 0, regions: 0, missing: 0 });
  useEffect(() => {
    const t = setTimeout(() => {
      sound.clear();
      const r = range;
      if (!r || op.isEmptyRange(r)) { useEdit.getState().set({ stripPreview: null }); setCount({ clips: 0, regions: 0, missing: 0 }); return; }
      let missing = 0, regions = 0, n = 0;
      for (const c of clips) {
        if (c.kind !== 'audio' || !r.trackIds.includes(c.trackId) || c.start >= r.end || op.clipEnd(c) <= r.start) continue;
        const b = c.sourceId ? engine.buffers.get(c.sourceId) : undefined;
        if (!b) { missing++; continue; }
        const reg = detectSound(channels(b), b.sampleRate, c, opts).map(([a, e]) => [Math.max(a, r.start), Math.min(e, r.end)] as [number, number]).filter(([a, e]) => e > a);
        sound.set(c.id, reg); regions += reg.length; n++;
      }
      useEdit.getState().set({ stripPreview: new Map(sound) });
      setCount({ clips: n, regions, missing });
    }, 60);
    return () => clearTimeout(t);
  }, [opts, range, clips, sound]);
  useEffect(() => () => useEdit.getState().set({ stripPreview: null }), []);
  const apply = (mode: op.StripMode) => {
    const r = range;
    if (!r || !sound.size) return toast('Keine Audio-Clips in der Auswahl');
    let n = 0;
    commit(mode === 'strip' ? 'Strip Silence' : mode === 'extract' ? 'Strip Silence: Extract' : 'Strip Silence: Separate', (p) => { n = op.stripSilence(p, r, sound, mode, naming.on ? naming : undefined); });
    toast(mode === 'strip' ? `${n} Clips mit Signal bleiben, die Stille ist entfernt. Rückgängig oder Heal stellt alles wieder her.` : mode === 'extract' ? 'Signal entfernt, die Stille (Raumton) bleibt' : `In ${n} Clips geteilt`);
  };
  return (
    <Shell title="Strip Silence" sub="Edit > Strip Silence (⌘U)" onClose={onClose} side>
      <div className="stack" style={{ gap: 10 }}>
        <Slider label="Schwelle" value={opts.thresholdDb} min={-96} max={0} step={0.5} unit="dB" onChange={(v) => set({ thresholdDb: v })} />
        <Slider label="Min. Stille" value={opts.minStripMs} min={0} max={4000} step={10} unit="ms" onChange={(v) => set({ minStripMs: v })} />
        <Slider label="Vorlauf" value={opts.padStartMs} min={0} max={1000} step={1} unit="ms" onChange={(v) => set({ padStartMs: v })} />
        <Slider label="Nachlauf" value={opts.padEndMs} min={0} max={2000} step={1} unit="ms" onChange={(v) => set({ padEndMs: v })} />
        <div className="t2" style={{ fontSize: 12 }}>
          {!range || op.isEmptyRange(range) ? 'Erst in der Timeline einen Bereich auf Audio-Spuren auswählen.' : `${count.regions} Bereiche mit Signal in ${count.clips} Clips (orange umrandet).`}
          {count.missing > 0 && ` ${count.missing} Clips sind noch nicht geladen.`}
        </div>
        <details className="naming" open={naming.on} onToggle={(e) => setNaming({ ...naming, on: (e.target as HTMLDetailsElement).open })}>
          <summary>Neue Clips benennen (Rename)</summary>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            <label className="etl">Name<input className="input" value={naming.name} onChange={(e) => setNaming({ ...naming, name: e.target.value })} style={{ width: 90 }} /></label>
            <label className="etl">Start<input className="input" type="number" value={naming.start} onChange={(e) => setNaming({ ...naming, start: Number(e.target.value) })} style={{ width: 54 }} /></label>
            <label className="etl">Nullen<input className="input" type="number" min={0} max={4} value={naming.zeros} onChange={(e) => setNaming({ ...naming, zeros: Number(e.target.value) })} style={{ width: 44 }} /></label>
            <label className="etl">Endung<input className="input" value={naming.suffix} onChange={(e) => setNaming({ ...naming, suffix: e.target.value })} style={{ width: 60 }} /></label>
          </div>
          <div className="t2" style={{ fontSize: 11.5, marginTop: 4 }}>Beispiel: {naming.name}{String(naming.start).padStart(naming.zeros + 1, '0')}{naming.suffix}</div>
        </details>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
          <button className="btn small" title="Signal entfernen, Stille behalten (z. B. für Raumton)" onClick={() => apply('extract')}>Extract</button>
          <button className="btn small" title="An den erkannten Grenzen teilen, nichts entfernen" onClick={() => apply('separate')}>Separate</button>
          <button className="btn small primary" title="Stille entfernen, Clips mit Signal bleiben" onClick={() => apply('strip')}>Strip</button>
        </div>
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Fades (Kap. 30)
const SHAPES: { id: FadeShape; label: string }[] = [
  { id: 'linear', label: 'Linear' }, { id: 'equalPower', label: 'Gleiche Leistung' }, { id: 'sCurve', label: 'S-Kurve' }, { id: 'exp', label: 'Exponentiell' }, { id: 'log', label: 'Logarithmisch' },
];
function Curve({ shape, out, x }: { shape: FadeShape; out?: boolean; x?: FadeShape }) {
  const pts = (s: FadeShape, inv: boolean) => Array.from({ length: 25 }, (_, i) => { const u = i / 24; return `${2 + u * 56},${30 - fadeCurve(s, inv ? 1 - u : u) * 26}`; }).join(' ');
  return (
    <svg width={60} height={34} className="curve">
      {x ? <><polyline points={pts(x, false)} /><polyline points={pts(x, true)} className="b" /></> : <polyline points={pts(shape, !!out)} />}
    </svg>
  );
}
function ShapePick({ label, value, onChange, out, cross }: { label: string; value: FadeShape; onChange: (s: FadeShape) => void; out?: boolean; cross?: boolean }) {
  return (
    <div className="shp">
      <b>{label}</b>
      <div className="shp-row">{SHAPES.map((s) => <button key={s.id} className={value === s.id ? 'on' : ''} title={s.label} onClick={() => onChange(s.id)}>{cross ? <Curve shape={s.id} x={s.id} /> : <Curve shape={s.id} out={out} />}<small>{s.label}</small></button>)}</div>
    </div>
  );
}
function Fades({ onClose }: { onClose: () => void }) {
  const f = useEdit((s) => s.fades);
  const set = (p: Partial<typeof f>) => useEdit.getState().set({ fades: { ...f, ...p } });
  const apply = () => {
    const r = currentRange();
    if (!r || op.isEmptyRange(r)) return toast('Erst einen Bereich auswählen');
    let res = { fadeIns: 0, fadeOuts: 0, crossfades: 0 };
    useStudio.getState().commit('Fades', (p) => { res = op.applyFades(p, r, f, srcLen); });
    const n = res.fadeIns + res.fadeOuts + res.crossfades;
    toast(n ? [res.fadeIns && `${res.fadeIns} Fade-In`, res.fadeOuts && `${res.fadeOuts} Fade-Out`, res.crossfades && `${res.crossfades} Crossfade`].filter(Boolean).join(', ') : 'Die Auswahl muss über einem Clip-Anfang, einem Clip-Ende oder einer Schnittstelle liegen');
    if (n) onClose();
  };
  return (
    <Shell title="Fades" sub="Über einem Clip-Anfang: Fade-In · über einem Ende: Fade-Out · über einer Schnittstelle: Crossfade" onClose={onClose} wide>
      <div className="stack" style={{ gap: 12 }}>
        <ShapePick label="Fade-In" value={f.inShape} onChange={(s) => set({ inShape: s })} />
        <ShapePick label="Fade-Out" value={f.outShape} out onChange={(s) => set({ outShape: s })} />
        <ShapePick label="Crossfade" value={f.xShape} cross onChange={(s) => set({ xShape: s })} />
        <div className="t2" style={{ fontSize: 12 }}>Für Crossfades zwischen Takes ist „Gleiche Leistung“ meist richtig, für Fades am Anfang und Ende die S-Kurve. Fades lassen sich auch direkt in der Timeline ziehen: oben an den Ecken eines Clips (Smart Tool).</div>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
          <button className="btn small" onClick={() => { run('fadesRemove'); onClose(); }}>Fades entfernen</button>
          <button className="btn small primary" onClick={apply}>Anwenden</button>
        </div>
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
function Repeat({ onClose }: { onClose: () => void }) {
  const [n, setN] = useState(3);
  const apply = () => {
    const r = currentRange(); if (!r || op.isEmptyRange(r)) return;
    let sel = r;
    useStudio.getState().commit(`Wiederholen ×${n}`, (p) => { sel = op.repeat(p, r, n, useEdit.getState().mode === 'shuffle'); });
    useEdit.getState().setRange(sel); onClose();
  };
  return (
    <Shell title="Wiederholen" sub="⌥R: Auswahl mehrfach hintereinander" onClose={onClose}>
      <form className="row" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); apply(); }}>
        <label className="etl">Anzahl<input autoFocus className="input" type="number" min={1} max={128} value={n} onChange={(e) => setN(Math.max(1, Math.min(128, Number(e.target.value))))} style={{ width: 70 }} /></label>
        <button className="btn small primary" type="submit">OK</button>
      </form>
    </Shell>
  );
}

function Spot({ onClose }: { onClose: () => void }) {
  const id = useEdit((s) => s.dialogArg);
  const p = useStudio((s) => s.project);
  const c = p.clips.find((x) => x.id === id);
  const [text, setText] = useState(c ? fmtBBT(p, c.start) : '1.1.000');
  if (!c) return null;
  const t = parseTime(p, text);
  const apply = () => {
    if (t === null) return toast('Format: Takt.Schlag.Tick (z. B. 9.1.000) oder Minuten:Sekunden (1:23,5)');
    if (c.locked) return toast('Der Clip ist gesperrt (Time Lock)');
    useStudio.getState().commit('Spotten', (q) => { const x = q.clips.find((y) => y.id === c.id); if (x) x.start = Math.max(0, t); });
    onClose();
  };
  return (
    <Shell title="Spot" sub={`${c.name}: Anfang auf eine genaue Position setzen`} onClose={onClose}>
      <form className="stack" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); apply(); }}>
        <label className="etl">Anfang<input autoFocus className="input mono" value={text} onChange={(e) => setText(e.target.value)} style={{ width: 120 }} /></label>
        <div className="t2" style={{ fontSize: 12 }}>{t !== null ? `= ${t.toFixed(3).replace('.', ',')} s` : 'Takt.Schlag.Tick oder Minuten:Sekunden'} · bisher {fmtBBT(p, c.start)}</div>
        <div className="row" style={{ justifyContent: 'flex-end' }}><button className="btn small primary" type="submit">OK</button></div>
      </form>
    </Shell>
  );
}

function Gain({ onClose }: { onClose: () => void }) {
  const clips = targetClips();
  const [db, setDb] = useState(clips[0]?.gainDb ?? 0);
  const ids = clips.map((c) => c.id);
  return (
    <Shell title="Clip Gain" sub={`${clips.length} ${clips.length === 1 ? 'Clip' : 'Clips'}. Wirkt vor Inserts und Fader, zerstörungsfrei.`} onClose={onClose}>
      <div className="stack" style={{ gap: 10 }}>
        <Slider label="Gain" value={db} min={-36} max={36} step={0.1} unit="dB" onChange={(v) => { setDb(v); useStudio.getState().commit('Clip Gain', (p) => op.setClipGain(p, ids, v), { merge: true }); }} />
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span className="t2" style={{ fontSize: 12 }}>⌃⇧↑ / ⌃⇧↓ ändert in 1-dB-Schritten</span>
          <div className="row" style={{ gap: 6 }}>
            <button className="btn small" onClick={() => { setDb(0); useStudio.getState().commit('Clip Gain', (p) => op.setClipGain(p, ids, 0)); }}>0 dB</button>
            <button className="btn small primary" onClick={() => { run('normalize'); onClose(); }}>Normalisieren</button>
          </div>
        </div>
      </div>
    </Shell>
  );
}

function Rename({ onClose }: { onClose: () => void }) {
  const clips = targetClips();
  const [name, setName] = useState(clips[0]?.name ?? '');
  const [num, setNum] = useState(clips.length > 1);
  return (
    <Shell title="Clip umbenennen" sub={`${clips.length} ${clips.length === 1 ? 'Clip' : 'Clips'}`} onClose={onClose}>
      <form className="stack" style={{ gap: 8 }} onSubmit={(e) => { e.preventDefault(); useStudio.getState().commit('Umbenennen', (p) => op.renameClips(p, clips.map((c) => c.id), name.trim() || 'Clip', num)); onClose(); }}>
        <input autoFocus className="input" value={name} onChange={(e) => setName(e.target.value)} />
        {clips.length > 1 && <label className="row" style={{ gap: 6, fontSize: 12.5 }}><input type="checkbox" checked={num} onChange={(e) => setNum(e.target.checked)} />durchnummerieren (01, 02 …)</label>}
        <div className="row" style={{ justifyContent: 'flex-end' }}><button className="btn small primary" type="submit">OK</button></div>
      </form>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// AudioSuite: Clips offline bearbeiten (neue Audiodatei, ⌘Z holt das Original zurück)
type SuiteTarget = { clip: Clip; a: number; b: number };
function suiteTargets(p: Project, r: op.Range | null): SuiteTarget[] {
  if (!r) return [];
  const sel = useStudio.getState().selection.clipIds;
  if (op.isEmptyRange(r)) return p.clips.filter((c) => c.kind === 'audio' && sel.includes(c.id)).map((c) => ({ clip: c, a: c.start, b: op.clipEnd(c) }));
  return p.clips.filter((c) => c.kind === 'audio' && r.trackIds.includes(c.trackId) && c.start < r.end - 1e-4 && op.clipEnd(c) > r.start + 1e-4)
    .map((c) => ({ clip: c, a: Math.max(c.start, r.start), b: Math.min(op.clipEnd(c), r.end) }));
}
function AudioSuite({ onClose }: { onClose: () => void }) {
  const p = useStudio((s) => s.project);
  const range = useEdit((s) => s.range);
  const commit = useStudio((s) => s.commit);
  const arg = useEdit((s) => s.dialogArg);
  const [kind, setKind] = useState<SuiteOp['kind']>((arg as SuiteOp['kind']) || 'pitch');
  const [semis, setSemis] = useState(0), [cents, setCents] = useState(0), [mode, setMode] = useState<StretchMode>('polyphonic');
  const [livePitch, setLivePitch] = useState(true);
  const [how, setHow] = useState<'pct' | 'tempo'>('pct'), [pct, setPct] = useState(100), [fromBpm, setFromBpm] = useState(String(p.bpm));
  const [vari, setVari] = useState(100);
  const [slotId, setSlotId] = useState(''), [tail, setTail] = useState(0), [bypassAfter, setBypassAfter] = useState(true);
  const [busy, setBusy] = useState('');
  const targets = suiteTargets(p, range);
  const tracks = [...new Set(targets.map((t) => t.clip.trackId))].map((id) => p.tracks.find((t) => t.id === id)!).filter(Boolean);
  const allElastic = tracks.length > 0 && tracks.every((t) => t.elastic);
  const slots = tracks[0]?.inserts.filter((x) => pluginDef(x.plugin) && !x.plugin.startsWith('bridge:')) ?? [];
  const slot = slots.find((x) => x.id === slotId) ?? slots[0];
  const at = targets[0]?.a ?? 0;
  const songBpm = bpmAt(p, at);
  const opNow = (): SuiteOp | null => {
    if (kind === 'pitch') return { kind, semitones: semis + cents / 100, mode };
    if (kind === 'stretch') { const ratio = how === 'pct' ? pct / 100 : Number(fromBpm.replace(',', '.')) / songBpm; return ratio > 0.2 && ratio < 5 ? { kind, ratio, mode } : null; }
    if (kind === 'varispeed') return { kind, ratio: vari / 100 };
    if (kind === 'plugin') return slot ? { kind, slot, tail } : null;
    return { kind } as SuiteOp;
  };
  const elasticPitch = kind === 'pitch' && allElastic && livePitch;
  const preview = async () => {
    const o = opNow(), t = targets[0];
    if (!o || !t) return;
    setBusy('Vorhören …');
    try {
      const inp = await clipAudio(p, t.clip, t.a, Math.min(t.b, t.a + 20));
      if (!inp) return toast('Audio noch nicht geladen');
      const out = toBuffer(await processAudio(inp, o, songBpm));
      const ctx = await engine.ensure(), src = ctx.createBufferSource(); src.buffer = out;
      src.connect(engine.graph!.out); src.start(); toast('Vorhören: erster Clip, bis 20 s');
    } finally { setBusy(''); }
  };
  const apply = async () => {
    const o = opNow();
    if (!o) return toast(kind === 'plugin' ? 'Die Spur hat kein eingebautes Plugin als Insert' : 'Wert prüfen');
    if (!targets.length) return toast('Erst Audio-Clips oder einen Bereich auf Audiospuren auswählen');
    const r = range!, ids = tracks.map((t) => t.id);
    const split = (q: Project) => { if (!op.isEmptyRange(r)) { op.splitTracksAt(q, ids, r.start); op.splitTracksAt(q, ids, r.end); } };
    const find = (q: Project, t: SuiteTarget) => q.clips.find((c) => c.trackId === t.clip.trackId && c.kind === 'audio' && Math.abs(c.start - t.a) < 1e-4);
    if (elasticPitch) {
      commit(`Elastic Pitch ${o.kind === 'pitch' ? o.semitones : 0} HT`, (q) => { split(q); for (const t of targets) { const c = find(q, t); if (c) setPitch(q, c, (o as { semitones: number }).semitones); } });
      toast('Tonhöhe gesetzt (Elastic Pitch, jederzeit wieder änderbar)');
      return onClose();
    }
    setBusy('Rechne …');
    try {
      const results: { t: SuiteTarget; id: string; buf: AudioBuffer }[] = [];
      for (const t of targets) {
        const inp = await clipAudio(p, t.clip, t.a, t.b);
        if (!inp) continue;
        const buf = toBuffer(await processAudio(inp, o, bpmAt(p, t.a)));
        const id = uid('src'); engine.buffers.set(id, buf); results.push({ t, id, buf });
      }
      const label = SUITE_OPS.find((x) => x.id === o.kind)!.label;
      commit(`AudioSuite: ${label}`, (q) => {
        split(q);
        for (const { t, id, buf } of results) {
          const c = find(q, t);
          if (!c) continue;
          c.sourceId = id; c.offset = 0; c.duration = buf.duration; c.loop = undefined; delete c.warp;
          c.fadeIn = Math.min(c.fadeIn, c.duration / 2); c.fadeOut = Math.min(c.fadeOut, c.duration / 2);
          if (q.tracks.find((x) => x.id === c.trackId)?.elastic) ensureWarp(q, c);
        }
        if (o.kind === 'plugin' && bypassAfter) for (const tr of q.tracks) if (ids.includes(tr.id)) for (const sl of tr.inserts) if (sl.id === o.slot.id) sl.bypass = true;
      });
      const st = useStudio.getState();
      if (st.token) for (const { id, buf } of results) void api.uploadWav(encodeWav(buf), `${id}.wav`, st.project.id).then((m) => { if (m) { engine.buffers.set(m.id, buf); useStudio.getState().replaceSourceId(id, m.id); } }).catch(() => undefined);
      toast(`${label}: ${results.length} ${results.length === 1 ? 'Clip' : 'Clips'} bearbeitet. Rückgängig mit ⌘Z.`);
      onClose();
    } finally { setBusy(''); }
  };
  const modeSel = <select className="input" value={mode} onChange={(e) => setMode(e.target.value as StretchMode)}>{STRETCH_MODES.filter((m) => m.id !== 'varispeed').map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}</select>;
  return (
    <Shell title="AudioSuite" sub="Clips bearbeiten und als neue Audiodatei ablegen" onClose={onClose} wide>
      <div className="suite">
        <div className="suite-list">
          {SUITE_OPS.map((x) => <button key={x.id} className={kind === x.id ? 'on' : ''} onClick={() => setKind(x.id)}><b>{x.label}</b><span>{x.hint}</span></button>)}
        </div>
        <div className="stack" style={{ gap: 10 }}>
          <div className="bd-sel">{targets.length ? <><span>{targets.length} {targets.length === 1 ? 'Clip' : 'Clips'}</span><span className="t2">auf {tracks.map((t) => t.name).join(', ')}</span></> : <span className="t2">Erst Audio-Clips anklicken oder einen Bereich auf Audiospuren auswählen.</span>}</div>
          {kind === 'pitch' && <>
            <Slider label="Halbtöne" value={semis} min={-24} max={24} unit="HT" onChange={setSemis} />
            <Slider label="Cent" value={cents} min={-100} max={100} unit="ct" onChange={setCents} />
            <label className="sl"><span>Algorithmus</span>{modeSel}</label>
            {allElastic && <label className="row" style={{ gap: 6, fontSize: 12.5 }}><input type="checkbox" style={{ width: 'auto' }} checked={livePitch} onChange={(e) => setLivePitch(e.target.checked)} />Als Elastic Pitch (bleibt jederzeit änderbar)</label>}
          </>}
          {kind === 'stretch' && <>
            <div className="seg"><button className={how === 'pct' ? 'on' : ''} onClick={() => setHow('pct')}>Um Prozent</button><button className={how === 'tempo' ? 'on' : ''} onClick={() => setHow('tempo')}>Von Tempo auf Songtempo</button></div>
            {how === 'pct' ? <Slider label="Neue Länge" value={pct} min={25} max={400} unit="%" onChange={setPct} />
              : <label className="sl"><span>Aufnahme hat</span><input className="input num" value={fromBpm} onChange={(e) => setFromBpm(e.target.value)} /><em style={{ gridColumn: 'span 2' }}>BPM → {String(songBpm).replace('.', ',')} BPM</em></label>}
            <label className="sl"><span>Algorithmus</span>{modeSel}</label>
          </>}
          {kind === 'varispeed' && <><Slider label="Geschwindigkeit" value={vari} min={50} max={200} unit="%" onChange={setVari} /><div className="t2" style={{ fontSize: 12 }}>Tonhöhe ändert sich um {(12 * Math.log2(vari / 100)).toFixed(1).replace('.', ',')} Halbtöne, die Länge auf {Math.round(10000 / vari)} %.</div></>}
          {kind === 'plugin' && <>
            {slots.length ? <label className="sl"><span>Insert</span><select className="input" style={{ gridColumn: 'span 3' }} value={slot?.id} onChange={(e) => setSlotId(e.target.value)}>{slots.map((x) => <option key={x.id} value={x.id}>{pluginDef(x.plugin)?.name ?? x.plugin}{x.bypass ? ' (umgangen)' : ''}</option>)}</select></label>
              : <div className="t2">Die Spur hat noch kein eingebautes Plugin. Im Mixer (X) ein Insert setzen und einstellen, dann hier anwenden.</div>}
            <Slider label="Nachhall" value={tail} min={0} max={10} step={0.5} unit="s" onChange={setTail} />
            <label className="row" style={{ gap: 6, fontSize: 12.5 }}><input type="checkbox" style={{ width: 'auto' }} checked={bypassAfter} onChange={(e) => setBypassAfter(e.target.checked)} />Insert danach umgehen (sonst wirkt er doppelt)</label>
          </>}
          {['reverse', 'invert', 'dc'].includes(kind) && <div className="t2" style={{ fontSize: 12 }}>{SUITE_OPS.find((x) => x.id === kind)?.hint}. Keine weiteren Einstellungen.</div>}
          <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
            {busy && <span className="t2" style={{ marginRight: 'auto', fontSize: 12 }}>{busy}</span>}
            {!elasticPitch && <button className="btn small" disabled={!!busy || !targets.length} onClick={() => void preview()}>Vorhören</button>}
            <button className="btn small primary" disabled={!!busy || !targets.length} onClick={() => void apply()}>{elasticPitch ? 'Tonhöhe setzen' : 'Anwenden'}</button>
          </div>
        </div>
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Commit: Spuren auf neue Audiospuren rechnen (Pro Tools: Commit)
function CommitDialog({ onClose }: { onClose: () => void }) {
  const p = useStudio((s) => s.project);
  const arg = useEdit((s) => s.dialogArg);
  const range = useEdit((s) => s.range);
  const ids = (arg ?? '').split(',').filter((id) => p.tracks.some((t) => t.id === id));
  const [inserts, setInserts] = useState(true), [original, setOriginal] = useState<'hide' | 'keep' | 'delete'>('hide');
  const [scope, setScope] = useState<'song' | 'sel'>(range && !op.isEmptyRange(range) ? 'sel' : 'song');
  const [busy, setBusy] = useState('');
  const go = async () => {
    setBusy('Rechne …');
    try {
      const r = scope === 'sel' && range && !op.isEmptyRange(range) ? { from: range.start, to: range.end } : {};
      const made = await commitTracks(ids, { ...r, inserts, original });
      toast(made.length ? `${made.length} ${made.length === 1 ? 'Spur' : 'Spuren'} auf neue Audiospur gerechnet${original === 'hide' ? ', Original ausgeblendet und stumm (Spurliste)' : ''}. Rückgängig mit ⌘Z.` : 'Keine Clips zum Rechnen');
      onClose();
    } finally { setBusy(''); }
  };
  return (
    <Shell title="Commit" sub="Spur auf neue Audiospur rechnen (Inserts, Instrument, Elastic)" onClose={onClose}>
      <div className="stack" style={{ gap: 10 }}>
        <div className="bd-sel"><span>{ids.length} {ids.length === 1 ? 'Spur' : 'Spuren'}</span><span className="t2">{ids.map((id) => p.tracks.find((t) => t.id === id)?.name).join(', ')}</span></div>
        <div className="seg"><button className={scope === 'song' ? 'on' : ''} onClick={() => setScope('song')}>Ganzer Song</button><button className={scope === 'sel' ? 'on' : ''} disabled={!range || op.isEmptyRange(range)} onClick={() => setScope('sel')}>Nur Auswahl</button></div>
        <label className="row" style={{ gap: 6, fontSize: 12.5 }}><input type="checkbox" style={{ width: 'auto' }} checked={inserts} onChange={(e) => setInserts(e.target.checked)} />Inserts einrechnen (sonst bleiben sie auf der neuen Spur)</label>
        <label className="sl"><span>Original</span><select className="input" style={{ gridColumn: 'span 3' }} value={original} onChange={(e) => setOriginal(e.target.value as 'hide' | 'keep' | 'delete')}><option value="hide">ausblenden und stumm</option><option value="keep">behalten</option><option value="delete">löschen</option></select></label>
        <div className="t2" style={{ fontSize: 12 }}>Die neue Spur übernimmt Fader, Pan, Sends, Ausgang und deren Automation. Gut für MIDI mit Instrument, schwere Plugin-Ketten und fertige Elastic-Spuren.</div>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>{busy && <span className="t2" style={{ marginRight: 'auto' }}>{busy}</span>}<button className="btn small primary" disabled={!!busy || !ids.length} onClick={() => void go()}>Commit</button></div>
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Export: Mixdown und Stems (Pro Tools: Bounce Mix, Track Bounce)
function ExportDialog({ onClose }: { onClose: () => void }) {
  const p = useStudio((s) => s.project);
  const range = useEdit((s) => s.range);
  const [scope, setScope] = useState<'song' | 'sel'>('song');
  const [mix, setMix] = useState(true), [mode, setMode] = useState<StemMode>('tracks');
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [effects, setEffects] = useState(true), [master, setMaster] = useState(false), [tail, setTail] = useState(2);
  const [busy, setBusy] = useState('');
  const items = stemItems(p, mode);
  const chosen = items.filter((x) => picked[`${mode}:${x.id}`]);
  const all = items.length > 0 && chosen.length === items.length;
  const go = async () => {
    const r = scope === 'sel' && range && !op.isEmptyRange(range) ? { from: range.start, to: range.end + tail } : { from: 0, to: defaultRange(p).to - 2 + tail };
    if (!mix && !chosen.length) return toast('Mix oder mindestens einen Stem auswählen');
    setBusy('Starte …');
    try {
      const res = await exportAudio(p, { ...r, mix, stems: chosen, effects, master }, setBusy);
      const a = document.createElement('a'); a.href = URL.createObjectURL(res.blob); a.download = res.name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 60000);
      toast(`${res.files} ${res.files === 1 ? 'Datei' : 'Dateien'} exportiert: ${res.name}`);
      onClose();
    } finally { setBusy(''); }
  };
  return (
    <Shell title="Export" sub="Mixdown und Stems als WAV, 24 Bit, 48 kHz (⌥⌘B)" onClose={onClose} wide>
      <div className="stack" style={{ gap: 10 }}>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <div className="seg"><button className={scope === 'song' ? 'on' : ''} onClick={() => setScope('song')}>Ganzer Song</button><button className={scope === 'sel' ? 'on' : ''} disabled={!range || op.isEmptyRange(range)} onClick={() => setScope('sel')}>Auswahl</button></div>
          <label className="etl">Nachlauf<input className="input num" type="number" min={0} max={20} value={tail} onChange={(e) => setTail(Math.max(0, Number(e.target.value) || 0))} style={{ width: 52 }} /> s</label>
          <label className="row" style={{ gap: 6, fontSize: 12.5, marginLeft: 'auto' }}><input type="checkbox" style={{ width: 'auto' }} checked={mix} onChange={(e) => setMix(e.target.checked)} /><b>Mixdown</b> (Stereo, mit Master)</label>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <b style={{ fontSize: 12.5 }}>Stems</b>
          <div className="seg">{([['tracks', 'je Spur'], ['groups', 'je Gruppe'], ['busses', 'je Bus']] as [StemMode, string][]).map(([k, l]) => <button key={k} className={mode === k ? 'on' : ''} onClick={() => setMode(k)}>{l}</button>)}</div>
          <button className="lnk" style={{ marginLeft: 'auto' }} onClick={() => setPicked((x) => ({ ...x, ...Object.fromEntries(items.map((i) => [`${mode}:${i.id}`, !all])) }))}>{all ? 'keine' : 'alle'}</button>
        </div>
        <div className="stemlist">
          {items.map((i) => <label key={i.id} className="row" style={{ gap: 6 }}><input type="checkbox" style={{ width: 'auto' }} checked={!!picked[`${mode}:${i.id}`]} onChange={(e) => setPicked((x) => ({ ...x, [`${mode}:${i.id}`]: e.target.checked }))} /><span>{i.name}</span><small className="t2">{i.trackIds.length > 1 ? `${i.trackIds.length} Spuren` : ''}</small></label>)}
          {!items.length && <div className="t2">Keine Spuren mit Clips.</div>}
        </div>
        <div className="row" style={{ gap: 14, flexWrap: 'wrap', fontSize: 12.5 }}>
          <label className="row" style={{ gap: 6 }}><input type="checkbox" style={{ width: 'auto' }} checked={effects} onChange={(e) => setEffects(e.target.checked)} />Effekte (Sends auf Hall, Delay …) in die Stems</label>
          <label className="row" style={{ gap: 6 }}><input type="checkbox" style={{ width: 'auto' }} checked={master} onChange={(e) => setMaster(e.target.checked)} />Master-Plugins auch auf Stems</label>
        </div>
        <div className="t2" style={{ fontSize: 12 }}>Stems beginnen alle bei 0 bzw. am Anfang der Auswahl und sind gleich lang, so passen sie in jeder DAW übereinander. Mehrere Dateien kommen als ZIP.</div>
        <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>{busy && <span className="t2" style={{ marginRight: 'auto' }}>{busy}</span>}<button className="btn small primary" disabled={!!busy} onClick={() => void go()}>Exportieren</button></div>
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Elastic Quantize: Anschläge auf Elastic-Spuren aufs Raster ziehen, ohne zu schneiden
function ElasticQuantize({ onClose }: { onClose: () => void }) {
  const p = useStudio((s) => s.project);
  const range = useEdit((s) => s.range);
  const commit = useStudio((s) => s.commit);
  const [grid, setGrid] = useState(useEdit.getState().grid === 'bar' ? '1/16' : useEdit.getState().grid);
  const [strength, setStrength] = useState(100), [swing, setSwing] = useState(0), [exclude, setExclude] = useState(0), [sens, setSens] = useState(60);
  const [onsets, setOnsets] = useState<Onset[]>([]);
  const sel = range && !op.isEmptyRange(range) ? { ...range, trackIds: range.trackIds.filter((id) => p.tracks.find((t) => t.id === id)?.elastic) } : null;
  useEffect(() => {
    const t = setTimeout(() => { setOnsets(sel && sel.trackIds.length ? detectRange(useStudio.getState().project, sel, (id) => engine.buffers.get(id), 'enhanced').onsets : []); }, 60);
    return () => clearTimeout(t);
  }, [sel?.start, sel?.end, sel?.trackIds.join(','), p.clips]); // eslint-disable-line react-hooks/exhaustive-deps
  const o = { sensitivity: sens, resolution: 'sub' as const, sub: grid, strength, excludeWithin: exclude, swing };
  const trig = useMemo(() => classify(p, onsets, o), [onsets, grid, sens, p.tempos, p.bpm]); // eslint-disable-line react-hooks/exhaustive-deps
  const moves = trig.map((t) => ({ t: t.t, target: conformTarget(p, t, o) }));
  useEffect(() => { useEdit.getState().set({ beatPreview: trig.map((t, i) => ({ t: t.t, cls: t.cls, to: moves[i].target })) }); }, [trig, strength, swing, exclude]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => useEdit.getState().set({ beatPreview: null }), []);
  const apply = () => {
    if (!sel?.trackIds.length) return toast('Erst einen Bereich auf Elastic-Spuren auswählen (Spurmenü → Elastic Audio)');
    let n = 0;
    commit('Audio quantisieren (Elastic)', (q) => { n = quantizeElastic(q, sel, moves); });
    toast(`${n} Anschläge aufs Raster gezogen, ohne Schnitte. Warp-Marker lassen sich in der Warp-Ansicht nachziehen.`);
    onClose();
  };
  return (
    <Shell title="Audio quantisieren" sub="Elastic Audio: Anschläge aufs Raster, ohne zu schneiden" onClose={onClose} side>
      <div className="stack" style={{ gap: 10 }}>
        <div className="bd-sel">{sel?.trackIds.length ? <><span>{trig.length} Anschläge</span><span className="t2">auf {sel.trackIds.length} Elastic-{sel.trackIds.length === 1 ? 'Spur' : 'Spuren'}</span></> : <span className="t2">Bereich auf Spuren mit Elastic Audio auswählen (Rechtsklick auf den Spurkopf → Elastic Audio).</span>}</div>
        <label className="sl"><span>Raster</span><select className="input" style={{ gridColumn: 'span 3' }} value={grid} onChange={(e) => setGrid(e.target.value)}>{op.GRIDS.filter((g) => g.id !== 'bar').map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}</select></label>
        <Slider label="Empfindlichkeit" value={sens} min={0} max={100} unit="%" onChange={setSens} />
        <Slider label="Stärke" value={strength} min={0} max={100} unit="%" onChange={setStrength} />
        <Slider label="Ausschließen" value={exclude} min={0} max={100} unit="%" onChange={setExclude} />
        <Slider label="Swing" value={swing} min={0} max={150} unit="%" onChange={setSwing} />
        <div className="t2" style={{ fontSize: 12 }}>Mehrere Spuren (z. B. alle Drum-Mikrofone) werden gemeinsam gezogen und bleiben phasengleich. Im Unterschied zu Beat Detective entstehen keine Schnitte und Lücken.</div>
        <div className="row" style={{ justifyContent: 'flex-end' }}><button className="btn small primary" disabled={!trig.length} onClick={apply}>Quantisieren</button></div>
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Vocal-Timing: Silben erkennen, aufs Raster oder auf eine Leitspur (Lead-Vocal) ziehen, mit Elastic Audio (ohne Schnitte)
function VocalTiming({ onClose }: { onClose: () => void }) {
  const p = useStudio((s) => s.project);
  const range = useEdit((s) => s.range);
  const commit = useStudio((s) => s.commit);
  const E = useEdit.getState();
  const [kind, setKind] = useState<'grid' | 'guide'>('grid');
  const [grid, setGrid] = useState(E.grid === 'bar' ? '1/16' : E.grid);
  const [strength, setStrength] = useState(80), [tol, setTol] = useState(10), [phrases, setPhrases] = useState(false), [sens, setSens] = useState(50);
  const [maxShift, setMaxShift] = useState(150);
  const audioTracks = p.tracks.filter((t) => t.kind === 'audio');
  const sel = range && !op.isEmptyRange(range) ? { ...range, trackIds: range.trackIds.filter((id) => audioTracks.some((t) => t.id === id)) } : null;
  // Leitspur: nur Spuren mit Audio im Bereich, bevorzugt Lead/Vox im Namen
  const hasAudio = (id: string) => !!sel && p.clips.some((c) => c.trackId === id && c.kind === 'audio' && c.start < sel.end && c.start + c.duration > sel.start);
  const guides = audioTracks.filter((t) => !sel?.trackIds.includes(t.id)).sort((a, b) => Number(hasAudio(b.id)) - Number(hasAudio(a.id)));
  const [guideId, setGuideId] = useState(() => guides.find((t) => hasAudio(t.id) && /lead|vox|vocal|gesang/i.test(t.name))?.id ?? guides.find((t) => hasAudio(t.id))?.id ?? '');
  const [plans, setPlans] = useState<TimingPlan[]>([]);
  const [busy, setBusy] = useState(false);
  const target: TimingTarget = kind === 'grid' ? { kind, opts: { grid, strength: strength / 100, tolMs: tol, phrasesOnly: phrases } } : { kind, guideId, maxShiftMs: maxShift, strength: strength / 100 };
  useEffect(() => {
    if (!sel?.trackIds.length || (kind === 'guide' && !guideId)) { setPlans([]); return; }
    setBusy(true);
    const t = setTimeout(() => { try { setPlans(planTiming(useStudio.getState().project, sel, target, sens)); } finally { setBusy(false); } }, 80);
    return () => clearTimeout(t);
  }, [sel?.start, sel?.end, sel?.trackIds.join(','), kind, grid, strength, tol, phrases, sens, guideId, maxShift, p.clips]); // eslint-disable-line react-hooks/exhaustive-deps
  const all = plans.flatMap((x) => x.events);
  const moved = all.filter((e) => Math.abs(e.target - e.t) > 0.001);
  const avg = moved.length ? moved.reduce((s2, e) => s2 + Math.abs(e.target - e.t), 0) / moved.length : 0;
  useEffect(() => { useEdit.getState().set({ beatPreview: plans.flatMap((x) => x.events.map((e) => ({ t: e.t, cls: (x.onsets.some((o) => Math.abs(o.t - e.t) < 1e-3 && o.phrase) ? 'bar' : 'beat') as 'bar' | 'beat', to: e.target }))) }); }, [plans]);
  useEffect(() => () => useEdit.getState().set({ beatPreview: null }), []);
  const apply = () => {
    if (!sel?.trackIds.length) return toast('Erst einen Bereich auf der Vocal-Spur auswählen');
    if (!all.length) return toast('Keine Silben erkannt. Empfindlichkeit erhöhen.');
    let n = 0;
    commit(kind === 'grid' ? 'Vocal-Timing: aufs Raster' : 'Vocal-Timing: auf die Leitspur', (q) => {
      for (const pl of plans) {
        const t = q.tracks.find((x) => x.id === pl.trackId);
        if (!t) continue;
        if (!t.elastic) setElastic(q, t.id, 'monophonic');
        n += quantizeElastic(q, { start: sel.start, end: sel.end, trackIds: [pl.trackId] }, pl.events);
      }
    });
    toast(`Vocal-Timing: ${n} Stellen gezogen (Elastic Audio, ohne Schnitte). Feinarbeit in der Warp-Ansicht, Rückgängig mit ⌘Z.`, 6000);
    onClose();
  };
  const guideName = audioTracks.find((t) => t.id === guideId)?.name;
  return (
    <Shell title="Vocal-Timing" sub="Silben aufs Raster oder auf das Lead-Vocal ziehen, ohne zu schneiden" onClose={onClose} side>
      <div className="stack" style={{ gap: 10 }}>
        <div className="seg small" style={{ alignSelf: 'flex-start' }}>
          <button className={kind === 'grid' ? 'on' : ''} onClick={() => setKind('grid')}>Aufs Raster</button>
          <button className={kind === 'guide' ? 'on' : ''} onClick={() => setKind('guide')}>Auf Leitspur (Doubles, Backings)</button>
        </div>
        <div className="bd-sel">{sel?.trackIds.length
          ? <><span>{busy ? 'Analysiere …' : `${all.length} Stellen, ${moved.length} werden gezogen`}</span><span className="t2">{moved.length ? `im Schnitt ${Math.round(avg * 1000)} ms` : ''} · {sel.trackIds.length} {sel.trackIds.length === 1 ? 'Spur' : 'Spuren'}</span></>
          : <span className="t2">Bereich auf der Vocal-Spur auswählen (mehrere Spuren: alle werden einzeln gezogen).</span>}</div>
        {kind === 'grid' ? <>
          <label className="sl"><span>Raster</span><select className="input" style={{ gridColumn: 'span 3' }} value={grid} onChange={(e) => setGrid(e.target.value)}>{op.GRIDS.filter((g) => g.id !== 'bar').map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}</select></label>
          <Slider label="Stärke" value={strength} min={0} max={100} unit="%" onChange={setStrength} />
          <Slider label="Toleranz" value={tol} min={0} max={60} unit="ms" onChange={setTol} />
          <label className="row" style={{ gap: 6, fontSize: 12.5 }}><input type="checkbox" style={{ width: 'auto' }} checked={phrases} onChange={(e) => setPhrases(e.target.checked)} />Nur Phrasenanfänge ziehen (Silben dazwischen wandern mit, das Feeling bleibt)</label>
        </> : <>
          <label className="sl"><span>Leitspur</span><select className="input" style={{ gridColumn: 'span 3' }} value={guideId} onChange={(e) => setGuideId(e.target.value)}>{!guideId && <option value="">Spur wählen</option>}{guides.map((t) => <option key={t.id} value={t.id} disabled={!hasAudio(t.id)}>{t.name}{hasAudio(t.id) ? '' : ' (hier kein Audio)'}</option>)}</select></label>
          {guideId && !hasAudio(guideId) && <div className="t2" style={{ fontSize: 12, color: 'var(--red, #b42318)' }}>Auf der Leitspur liegt in diesem Bereich kein Audio.</div>}
          <Slider label="Stärke" value={strength} min={0} max={100} unit="%" onChange={setStrength} />
          <Slider label="Max. Versatz" value={maxShift} min={40} max={400} step={10} unit="ms" onChange={setMaxShift} />
        </>}
        <Slider label="Empfindlichkeit" value={sens} min={0} max={100} unit="%" onChange={setSens} />
        <div className="t2" style={{ fontSize: 12 }}>{kind === 'grid'
          ? 'Orange: erkannte Silben (dunkel = Phrasenanfang), blau: Ziel. Toleranz lässt Silben stehen, die schon nah genug am Raster liegen. Unter 100 % Stärke bleibt es menschlich.'
          : `Die ausgewählten Spuren folgen ${guideName ? `„${guideName}“` : 'der Leitspur'} Silbe für Silbe, auch innerhalb langer Töne. Max. Versatz begrenzt, wie weit gesucht wird.`}</div>
        <div className="row" style={{ justifyContent: 'flex-end' }}><button className="btn small primary" disabled={!moved.length || busy} onClick={apply}>Anwenden</button></div>
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Tempo und Taktart (Pro Tools: Tempo- und Meter-Lineal, Event List)
function TempoMeter({ onClose }: { onClose: () => void }) {
  const p = useStudio((s) => s.project);
  const commit = useStudio((s) => s.commit);
  const arg = useEdit((s) => s.dialogArg);
  const at = Math.max(0, Number(arg ?? engine.position) || 0);
  const pos = bbt(p, at + 1e-7);
  const [tBar, setTBar] = useState(pos.bar), [tBeat, setTBeat] = useState(pos.beat), [tBpm, setTBpm] = useState(String(bpmAt(p, at)).replace('.', ','));
  const [mBar, setMBar] = useState(pos.beat === 1 ? pos.bar : pos.bar + 1), [num, setNum] = useState(pos.num), [den, setDen] = useState(pos.den);
  const tempos = tempoEvents(p), meters = meterEvents(p);
  const beatOf = (bar: number, beat: number) => barToBeat(p, bar - 1) + (beat - 1) * (4 / meterAtBar(p, bar - 1).den);
  const fmtPos = (beat: number) => { const x = bbt(p, secAt(p, beat) + 1e-7); return `${x.bar}|${x.beat}`; };
  const addTempo = () => {
    const v = Number(tBpm.replace(',', '.'));
    if (!(v >= 20 && v <= 300)) return toast('Tempo zwischen 20 und 300 BPM');
    const b = beatOf(Math.max(1, tBar), Math.max(1, tBeat));
    commit(b < 1e-6 ? 'Tempo' : `Tempowechsel bei Takt ${tBar}`, (q) => putTempo(q, b, v));
  };
  const addMeter = () => {
    if (!(num >= 1 && num <= 32)) return toast('Zähler zwischen 1 und 32');
    commit(mBar <= 1 ? 'Taktart' : `Taktwechsel bei Takt ${mBar}`, (q) => putMeter(q, mBar - 1, num, den));
  };
  return (
    <Shell title="Tempo und Taktart" sub="Tempo- und Taktwechsel im Song (⌥⇧T)" onClose={onClose} wide>
      <div className="tm">
        <section>
          <h4>Tempo</h4>
          <table className="table tmt"><tbody>
            {tempos.map((e) => (
              <tr key={`t${e.beat}`}><td className="mono">{fmtPos(e.beat)}</td><td className="mono">{String(e.bpm).replace('.', ',')} BPM</td>
                <td>{e.beat > 1e-6 ? <button className="lnk" title="Tempowechsel löschen" onClick={() => commit('Tempowechsel löschen', (q) => deleteTempo(q, e.beat))}>✕</button> : <span className="t2">Anfang</span>}</td></tr>
            ))}
          </tbody></table>
          <div className="row tmadd">
            <label className="etl">Takt<input className="input num" type="number" min={1} value={tBar} onChange={(e) => setTBar(Math.max(1, Number(e.target.value) || 1))} /></label>
            <label className="etl">Schlag<input className="input num" type="number" min={1} max={16} value={tBeat} onChange={(e) => setTBeat(Math.max(1, Number(e.target.value) || 1))} /></label>
            <label className="etl">BPM<input className="input num" value={tBpm} onChange={(e) => setTBpm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addTempo(); }} /></label>
            <button className="btn small primary" onClick={addTempo}>Setzen</button>
          </div>
        </section>
        <section>
          <h4>Taktart</h4>
          <table className="table tmt"><tbody>
            {meters.map((e) => (
              <tr key={`m${e.bar}`}><td className="mono">Takt {e.bar + 1}</td><td className="mono">{e.num}/{e.den}</td>
                <td>{e.bar > 0 ? <button className="lnk" title="Taktwechsel löschen" onClick={() => commit('Taktwechsel löschen', (q) => deleteMeter(q, e.bar))}>✕</button> : <span className="t2">Anfang</span>}</td></tr>
            ))}
          </tbody></table>
          <div className="row tmadd">
            <label className="etl">Ab Takt<input className="input num" type="number" min={1} value={mBar} onChange={(e) => setMBar(Math.max(1, Number(e.target.value) || 1))} /></label>
            <label className="etl">Takt<input className="input num" type="number" min={1} max={32} value={num} onChange={(e) => setNum(Number(e.target.value) || 4)} /></label>
            <span className="t2">/</span>
            <select className="input" value={den} onChange={(e) => setDen(Number(e.target.value))}>{[2, 4, 8, 16].map((d) => <option key={d} value={d}>{d}</option>)}</select>
            <button className="btn small primary" onClick={addMeter}>Setzen</button>
          </div>
        </section>
      </div>
      <div className="t2" style={{ fontSize: 12, marginTop: 10 }}>MIDI, Marker, Akkorde und Loop bleiben auf ihren Takten, Audio bleibt zeitlich, wo es ist. Für frei eingespielte Aufnahmen: Beat Detective (⌘8) → Tempo → „Raster anpassen“.</div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Takte einfügen und entfernen (Insert Time / Cut Time, Kap. 37)
function TimeOps({ onClose }: { onClose: () => void }) {
  const p = useStudio((s) => s.project);
  const r = useEdit((s) => s.range);
  const barOf = (t: number) => Math.floor(barAtSec(p, t) + 1e-6) + 1;
  const [at, setAt] = useState(barOf(r?.start ?? engine.position));
  const [count, setCount] = useState(r && !op.isEmptyRange(r) ? Math.max(1, Math.round(barAtSec(p, r.end) - barAtSec(p, r.start))) : 4);
  const [from, setFrom] = useState(barOf(r?.start ?? 0));
  const [to, setTo] = useState(r && !op.isEmptyRange(r) ? barOf(r.end - 1e-6) + 1 : barOf(0) + 4);
  // Eingefügte Takte haben Tempo und Taktart der Einfügestelle
  const insLen = (bar: number, n: number) => n * meterAtBar(p, bar - 1).barLen * spbAt(p, secOfBar(p, bar - 1));
  return (
    <Shell title="Takte einfügen oder entfernen" sub="Auf allen Spuren, mit Markern, Akkorden und Loop" onClose={onClose}>
      <div className="stack" style={{ gap: 14 }}>
        <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
          <label className="etl">Ab Takt<input className="input" type="number" min={1} value={at} onChange={(e) => setAt(Math.max(1, Number(e.target.value)))} style={{ width: 64 }} /></label>
          <label className="etl">Anzahl Takte<input className="input" type="number" min={1} value={count} onChange={(e) => setCount(Math.max(1, Number(e.target.value)))} style={{ width: 64 }} /></label>
          <button className="btn small primary" onClick={() => { useStudio.getState().commit(`${count} Takte einfügen`, (q) => op.insertTime(q, secOfBar(q, at - 1), insLen(at, count))); toast(`${count} Takte ab Takt ${at} eingefügt`); onClose(); }}>Einfügen</button>
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
          <label className="etl">Von Takt<input className="input" type="number" min={1} value={from} onChange={(e) => setFrom(Math.max(1, Number(e.target.value)))} style={{ width: 64 }} /></label>
          <label className="etl">bis vor Takt<input className="input" type="number" min={2} value={to} onChange={(e) => setTo(Math.max(from + 1, Number(e.target.value)))} style={{ width: 64 }} /></label>
          <button className="btn small danger" onClick={() => { useStudio.getState().commit(`Takte ${from}–${to - 1} entfernen`, (q) => op.cutTime(q, secOfBar(q, from - 1), secOfBar(q, to - 1))); toast(`Takte ${from} bis ${to - 1} entfernt, alles danach rückt nach`); onClose(); }}>Entfernen</button>
        </div>
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Memory Locations (Kap. 38)
function Markers({ onClose }: { onClose: () => void }) {
  const p = useStudio((s) => s.project);
  const focus = useEdit((s) => s.dialogArg);
  const commit = useStudio((s) => s.commit);
  const upd = (id: string, f: (m: Project['markers'][number]) => void, merge = true) => commit('Marker', (q) => { const m = q.markers.find((x) => x.id === id); if (m) f(m); q.markers.sort((a, b) => a.time - b.time); }, { merge });
  const all = p.tracks.map((t) => t.id);
  return (
    <Shell title="Memory Locations" sub="Marker und Bereiche (Strophe, Refrain …). M setzt einen Marker." onClose={onClose} wide>
      <table className="memtab">
        <thead><tr><th>#</th><th>Name</th><th>Anfang</th><th>Ende</th><th /></tr></thead>
        <tbody>
          {p.markers.map((m, i) => (
            <tr key={m.id}>
              <td className="mono">{i + 1}</td>
              <td><input className="input" autoFocus={focus === m.id} value={m.name} onChange={(e) => upd(m.id, (x) => { x.name = e.target.value; })} /></td>
              <td><TimeField p={p} value={m.time} onChange={(t) => upd(m.id, (x) => { if (x.end !== undefined) x.end += t - x.time; x.time = t; }, false)} /></td>
              <td>{m.end !== undefined ? <TimeField p={p} value={m.end} onChange={(t) => upd(m.id, (x) => { x.end = Math.max(x.time + 0.01, t); }, false)} /> : <button className="btn small" title="Zum Bereich machen (bis zum nächsten Marker)" onClick={() => upd(m.id, (x) => { x.end = p.markers[i + 1]?.time ?? secOfBar(p, barAtSec(p, x.time) + 4); }, false)}>Bereich</button>}</td>
              <td className="row" style={{ gap: 4 }}>
                <button className="btn small" onClick={() => engine.locate(m.time)}>Springen</button>
                <button className="btn small" title="Bereich auf allen Spuren auswählen" onClick={() => useEdit.getState().setRange({ start: m.time, end: m.end ?? p.markers[i + 1]?.time ?? secOfBar(p, barAtSec(p, m.time) + 4), trackIds: all })}>Auswählen</button>
                <button className="btn small danger" onClick={() => commit('Marker löschen', (q) => { q.markers = q.markers.filter((x) => x.id !== m.id); })}>✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="row" style={{ justifyContent: 'space-between', marginTop: 10 }}>
        <span className="t2" style={{ fontSize: 12 }}>Doppelklick ins Lineal zwischen zwei Marker wählt den Abschnitt auf allen Spuren aus.</span>
        <button className="btn small primary" onClick={() => run('marker')}>Neu an Abspielposition</button>
      </div>
    </Shell>
  );
}
function TimeField({ p, value, onChange }: { p: Project; value: number; onChange: (t: number) => void }) {
  const [text, setText] = useState(fmtBBT(p, value));
  useEffect(() => setText(fmtBBT(p, value)), [p, value]);
  return <input className="input mono" style={{ width: 92 }} value={text} onChange={(e) => setText(e.target.value)} onBlur={() => { const t = parseTime(p, text); if (t !== null) onChange(t); else setText(fmtBBT(p, value)); }} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />;
}

// ---------------------------------------------------------------------------
function Loop({ onClose }: { onClose: () => void }) {
  const id = useEdit((s) => s.dialogArg);
  const p = useStudio((s) => s.project);
  const c = p.clips.find((x) => x.id === id);
  const [mode, setMode] = useState<'count' | 'length'>('count');
  const [count, setCount] = useState(4);
  const [len, setLen] = useState(fmtLen(p, (c?.duration ?? 1) * 4, c?.start ?? 0));
  if (!c) return null;
  const apply = () => {
    if (mode === 'length') { const l = parseTime(p, len, true, c.start); if (!l) return toast('Länge als Takte.Schläge.Ticks, z. B. 8.0.000'); useStudio.getState().commit('Clip loopen', (q) => op.loopClip(q, c.id, { length: l })); }
    else useStudio.getState().commit('Clip loopen', (q) => op.loopClip(q, c.id, { count }));
    onClose();
  };
  return (
    <Shell title="Clip loopen" sub={`${c.name}: Inhalt wiederholen. Am rechten Rand mit ⌥ ziehen verlängert den Loop.`} onClose={onClose}>
      <form className="stack" style={{ gap: 10 }} onSubmit={(e) => { e.preventDefault(); apply(); }}>
        <label className="row" style={{ gap: 8 }}><input type="radio" checked={mode === 'count'} onChange={() => setMode('count')} />Anzahl Durchgänge<input className="input" type="number" min={1} max={512} value={count} onChange={(e) => { setMode('count'); setCount(Math.max(1, Number(e.target.value))); }} style={{ width: 70 }} /></label>
        <label className="row" style={{ gap: 8 }}><input type="radio" checked={mode === 'length'} onChange={() => setMode('length')} />Bis Länge<input className="input mono" value={len} onChange={(e) => { setMode('length'); setLen(e.target.value); }} style={{ width: 90 }} /><span className="t2" style={{ fontSize: 12 }}>Takte.Schläge.Ticks</span></label>
        <div className="row" style={{ justifyContent: 'flex-end' }}><button className="btn small primary" type="submit">OK</button></div>
      </form>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Event Operations für MIDI (Kap. 45)
function Events({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<'q' | 'v' | 'd' | 't'>('q');
  const grid = useEdit((s) => s.grid);
  const p = useStudio((s) => s.project);
  const g0 = op.GRIDS.find((x) => x.id === grid);
  const [q, setQ] = useState({ ...DEFAULT_QUANTIZE, grid: g0 && g0.beats > 0 ? g0.beats : 0.25 });
  const [vm, setVm] = useState<VelocityMode>('set');
  const [vv, setVv] = useState(100);
  const [dm, setDm] = useState<DurationMode>('legato');
  const [dv, setDv] = useState(100);
  const [semi, setSemi] = useState(12);
  const ids = targetClips().filter((c) => c.kind === 'midi').map((c) => c.id);
  const notes = p.clips.filter((c) => ids.includes(c.id)).reduce((n, c) => n + (c.notes?.length ?? 0), 0);
  const apply = () => {
    const label = { q: 'Quantisieren', v: 'Velocity', d: 'Notenlänge', t: 'Transponieren' }[tab];
    useStudio.getState().commit(label, (pr) => {
      for (const c of pr.clips) {
        if (!ids.includes(c.id) || !c.notes) continue;
        c.notes = tab === 'q' ? quantizeNotes(c.notes, q) : tab === 'v' ? changeVelocity(c.notes, vm, vv) : tab === 'd' ? changeDuration(c.notes, dm, dm === 'set' || dm === 'add' ? dv / 100 : dv) : transpose(c.notes, semi);
      }
    });
    toast(`${label}: ${notes} Noten in ${ids.length} ${ids.length === 1 ? 'Clip' : 'Clips'}`);
  };
  return (
    <Shell title="Event Operations" sub={`${ids.length} MIDI-Clips, ${notes} Noten`} onClose={onClose}>
      <div className="seg small" style={{ marginBottom: 12 }}>
        {([['q', 'Quantisieren'], ['v', 'Velocity'], ['d', 'Länge'], ['t', 'Transponieren']] as const).map(([k, l]) => <button key={k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{l}</button>)}
      </div>
      {tab === 'q' && <div className="stack" style={{ gap: 8 }}>
        <label className="etl">Raster<select className="input" value={q.grid} onChange={(e) => setQ({ ...q, grid: Number(e.target.value) })}>{op.GRIDS.filter((x) => x.beats > 0).map((x) => <option key={x.id} value={x.beats}>{x.label}</option>)}</select></label>
        <Slider label="Stärke" value={q.strength} min={0} max={100} unit="%" onChange={(v) => setQ({ ...q, strength: v })} />
        <Slider label="Swing" value={q.swing} min={0} max={100} unit="%" onChange={(v) => setQ({ ...q, swing: v })} />
        <Slider label="Bereich" value={q.window} min={1} max={100} unit="%" onChange={(v) => setQ({ ...q, window: v })} />
        <label className="row" style={{ gap: 6, fontSize: 12.5 }}><input type="checkbox" checked={q.ends} onChange={(e) => setQ({ ...q, ends: e.target.checked })} />Notenenden mitquantisieren</label>
      </div>}
      {tab === 'v' && <div className="stack" style={{ gap: 8 }}>
        <label className="etl">Art<select className="input" value={vm} onChange={(e) => { const m = e.target.value as VelocityMode; setVm(m); setVv(m === 'set' ? 100 : m === 'add' ? 10 : m === 'humanize' ? 8 : m === 'compress' ? 60 : 110); }}>
          <option value="set">auf Wert setzen</option><option value="add">addieren</option><option value="scale">skalieren (%)</option><option value="compress">zur Mitte stauchen (%)</option><option value="humanize">zufällig streuen (±)</option>
        </select></label>
        <Slider label="Wert" value={vv} min={vm === 'add' ? -64 : 0} max={vm === 'scale' || vm === 'compress' ? 200 : 127} unit={vm === 'scale' || vm === 'compress' ? '%' : ''} onChange={setVv} />
      </div>}
      {tab === 'd' && <div className="stack" style={{ gap: 8 }}>
        <label className="etl">Art<select className="input" value={dm} onChange={(e) => { const m = e.target.value as DurationMode; setDm(m); setDv(m === 'scale' ? 100 : m === 'set' ? 50 : 25); }}>
          <option value="legato">Legato (bis zur nächsten Note)</option><option value="set">feste Länge (1/100 Schlag)</option><option value="scale">skalieren (%)</option><option value="add">verlängern (1/100 Schlag)</option>
        </select></label>
        {dm !== 'legato' && <Slider label="Wert" value={dv} min={dm === 'add' ? -100 : 1} max={dm === 'scale' ? 400 : 800} unit={dm === 'scale' ? '%' : '/100'} onChange={setDv} />}
      </div>}
      {tab === 't' && <Slider label="Halbtöne" value={semi} min={-36} max={36} unit="HT" onChange={setSemi} />}
      <div className="row" style={{ justifyContent: 'flex-end', gap: 6, marginTop: 12 }}>
        <button className="btn small" onClick={onClose}>Schließen</button>
        <button className="btn small primary" disabled={!ids.length} onClick={apply}>Anwenden</button>
      </div>
    </Shell>
  );
}

function Shortcuts({ onClose }: { onClose: () => void }) {
  const groups = [...new Set(COMMANDS.map((c) => c.group))];
  return (
    <Shell title="Tastenkürzel" sub="Wie in Pro Tools, soweit der Browser es zulässt (⌘T, ⌘H, ⌘M sind vom Browser belegt)" onClose={onClose} wide>
      <div className="kbgrid">
        {groups.map((g) => <div key={g}><b>{g}</b>{COMMANDS.filter((c) => c.group === g && c.keys?.length).map((c) => <div key={c.id} className="kbrow"><span>{c.label}</span><kbd>{c.keys!.map((k) => keyLabel([k])).join(' / ')}</kbd></div>)}</div>)}
        <div><b>Transport</b>
          {[['Wiedergabe / Stopp', 'Leertaste'], ['Aufnahme', 'R'], ['Zum Anfang', 'Enter'], ['Loop an/aus', 'L'], ['Klick', 'K'], ['Mixer', 'X']].map(([a, b]) => <div key={a} className="kbrow"><span>{a}</span><kbd>{b}</kbd></div>)}
        </div>
        <div><b>Maus (Smart Tool)</b>
          {[['obere Hälfte eines Clips', 'Auswahl'], ['untere Hälfte', 'verschieben'], ['Ränder', 'trimmen'], ['obere Ecken', 'Fade ziehen'], ['⌥ beim Verschieben', 'kopieren'], ['⌥ am rechten Rand', 'Loop-Trim'], ['⌘ beim Ziehen', 'Raster aus/an'], ['Lineal ziehen', 'Auswahl auf allen Spuren'], ['Doppelklick ins Lineal', 'Abschnitt zwischen Markern']].map(([a, b]) => <div key={a} className="kbrow"><span>{a}</span><kbd>{b}</kbd></div>)}
        </div>
      </div>
    </Shell>
  );
}
